import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { rgba } from 'polished'
import styled from 'styled-components'

import { formatPrice } from '@/lib/format'
import { useTheme } from '@/lib/hooks'

const generateCashTransactions = () => {
  const txns = []
  const now = new Date()
  let balance = 200.00 // Starting float

  // Opening float
  txns.push({
    id: 'CSH-000',
    time: new Date(now.getTime() - 4 * 60 * 60 * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
    type: 'openingFloat',
    amount: 200.00,
    balance: 200.00,
  })

  // Random cash transactions
  for (let i = 1; i <= 8; i++) {
    const isPayment = Math.random() > 0.3
    const amount = parseFloat((Math.random() * 40 + 5).toFixed(2))
    if (isPayment) {
      balance += amount
      txns.push({
        id: `CSH-${String(i).padStart(3, '0')}`,
        time: new Date(now.getTime() - (8 - i) * 1000 * 60 * 20).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
        type: 'cashPayment',
        amount: amount,
        balance: parseFloat(balance.toFixed(2)),
      })
    } else {
      const changeAmt = parseFloat((Math.random() * 15 + 2).toFixed(2))
      balance -= changeAmt
      txns.push({
        id: `CSH-${String(i).padStart(3, '0')}`,
        time: new Date(now.getTime() - (8 - i) * 1000 * 60 * 20).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
        type: 'changeGiven',
        amount: -changeAmt,
        balance: parseFloat(balance.toFixed(2)),
      })
    }
  }

  return { txns, balance: parseFloat(balance.toFixed(2)) }
}

const CashTab = () => {
  const { t } = useTranslation()
  const { currency } = useTheme()
  const price = amount => formatPrice(amount, currency)
  const { txns, balance } = useMemo(() => generateCashTransactions(), [])
  const [printing, setPrinting] = useState(false)

  const handlePrint = () => {
    if (printing) return
    setPrinting(true)
    setTimeout(() => setPrinting(false), 2000)
  }

  return (
    <Wrapper>
      <Header>
        <SectionTitle>{t('cashTab.title')}</SectionTitle>
        <PrintBtn onClick={handlePrint} disabled={printing}>
          {printing ? t('cashTab.printing') : t('cashTab.printReport')}
        </PrintBtn>
      </Header>

      <CashSummary>
        <SummaryTile>
          <CashLabel>{t('cashTab.openingFloat')}</CashLabel>
          <CashValue>{price(200)}</CashValue>
        </SummaryTile>
        <SummaryTile>
          <CashLabel>{t('cashTab.cashPayments')}</CashLabel>
          <CashValue $positive>
            +{price(txns.filter(txn => txn.type === 'cashPayment').reduce((s, txn) => s + txn.amount, 0))}
          </CashValue>
        </SummaryTile>
        <SummaryTile>
          <CashLabel>{t('cashTab.changeGiven')}</CashLabel>
          <CashValue $negative>
            -{price(Math.abs(txns.filter(txn => txn.type === 'changeGiven').reduce((s, txn) => s + txn.amount, 0)))}
          </CashValue>
        </SummaryTile>
        <SummaryTile $total>
          <CashLabel>{t('cashTab.currentBalance')}</CashLabel>
          <CashValue $large>{price(balance)}</CashValue>
        </SummaryTile>
      </CashSummary>

      <SectionTitle as="h4" $sub>{t('cashTab.log')}</SectionTitle>
      <TableCard>
        <Table>
          <thead>
            <tr>
              <Th>{t('cashTab.columns.id')}</Th>
              <Th>{t('cashTab.columns.time')}</Th>
              <Th>{t('cashTab.columns.type')}</Th>
              <Th align="right">{t('cashTab.columns.amount')}</Th>
              <Th align="right">{t('cashTab.columns.balance')}</Th>
            </tr>
          </thead>
          <tbody>
            {txns.map(txn => (
              <tr key={txn.id}>
                <Td $muted>{txn.id}</Td>
                <Td>{txn.time}</Td>
                <Td>{t(`cashTab.type.${txn.type}`)}</Td>
                <Td align="right" $positive={txn.amount > 0} $negative={txn.amount < 0}>
                  {txn.amount >= 0 ? '+' : '-'}{price(Math.abs(txn.amount))}
                </Td>
                <Td align="right" $bold>{price(txn.balance)}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableCard>
    </Wrapper>
  )
}

export default CashTab

const POSITIVE = 'var(--color-success)'
const NEGATIVE = '#B3362B'

const Wrapper = styled.div`
  padding: 4px 0 8px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
`

const SectionTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 700;
  margin: ${({ $sub }) => $sub ? '24px 0 12px' : '0'};
  color: ${({ theme }) => theme.colors.global};
`

const PrintBtn = styled.button`
  min-height: 48px;
  padding: 0 20px;
  border: 1px solid var(--color-line);
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: inherit;
  white-space: nowrap;
  cursor: pointer;
  touch-action: manipulation;
  background: #ffffff;
  color: ${({ theme }) => theme.colors.global};
  transition: transform 0.1s ease-out, background-color 0.1s;

  &:not(:disabled):active {
    transform: scale(0.97);
    background: ${({ theme }) => rgba(theme.colors.global, 0.06)};
  }
  &:disabled { color: ${({ theme }) => theme.colors.muted}; cursor: wait; }
`

const CashSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
`

const SummaryTile = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
  min-height: 88px;
  padding: 14px 16px;
  background: #ffffff;
  border: 1px solid var(--color-line);
  border-radius: 14px;

  ${({ $total, theme }) => $total && `
    background: ${rgba(theme.colors.primary, 0.07)};
    border-color: ${rgba(theme.colors.primary, 0.35)};
  `}
`

const CashLabel = styled.span`
  font-size: 0.82rem;
  font-weight: 500;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.muted};
`

const CashValue = styled.span`
  font-size: 1.2rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: ${({ $positive, $negative, theme }) =>
    $positive ? POSITIVE : $negative ? NEGATIVE : theme.colors.global};
  ${({ $large }) => $large && 'font-size: 1.5rem;'}
`

const TableCard = styled.div`
  background: #ffffff;
  border: 1px solid var(--color-line);
  border-radius: var(--radius-card);
  overflow: hidden;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;

  tbody tr + tr td {
    border-top: 1px solid #EEF0F3;
  }
`

const Th = styled.th`
  text-align: ${({ align }) => align || 'left'};
  padding: 12px ${({ align }) => align === 'right' ? '24px' : '16px'} 12px 16px;
  font-size: 0.8125rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.muted};
  background: #FAFBFC;
  border-bottom: 1px solid var(--color-line);
`


const Td = styled.td`
  height: 52px;
  padding: 0 ${({ align }) => align === 'right' ? '24px' : '16px'} 0 16px;
  font-size: 0.92rem;
  text-align: ${({ align }) => align || 'left'};
  white-space: nowrap;
  font-weight: ${({ $bold, $positive, $negative }) => $bold ? '700' : ($positive || $negative) ? '600' : '400'};
  color: ${({ $muted, $positive, $negative, theme }) =>
    $muted ? theme.colors.muted : $positive ? POSITIVE : $negative ? NEGATIVE : theme.colors.global};
`
